import { useAuth } from '@pooflabs/web';
import { Bot, Inbox, KeyRound, LayoutGrid, Loader2, Pencil, Plus, Sparkles, Trash2, Wallet } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { AgentAvatar } from '@/components/AgentAvatar';
import TopNav from '@/components/TopNav';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { EmptyState, PageLayout } from '@/components/poof-ui';
import { NumberTicker } from '@/components/effects';
import { useRealtimeData } from '@/hooks/use-realtime-data';
import { aggregateAgents, deleteAgents, subscribeManyAgents, type AgentsResponse } from '@/lib/collections/agents';
import { aggregateCallRecords } from '@/lib/collections/callRecords';
import { isFunctionalAgentId } from '@/utils/agentContent';
import { formatCount, formatSol, lamportsToSol, timeGreeting } from '@/utils/format';

export const DashboardHomePage: React.FC = () => {
  const { user, login } = useAuth();
  const navigate = useNavigate();

  const { data: agents } = useRealtimeData<AgentsResponse[]>(subscribeManyAgents, true, 'limit 50');
  const { data: myAgents } = useRealtimeData<AgentsResponse[]>(
    subscribeManyAgents,
    !!user?.address,
    `creatorWallet='${user?.address ?? ''}' limit 50`
  );

  const [totalCalls, setTotalCalls] = useState(0);
  const [earningsSol, setEarningsSol] = useState(0);

  useEffect(() => {
    aggregateAgents('sum', { field: 'statsRuns' }).then((res) => setTotalCalls(res?.value ?? 0));
  }, []);

  useEffect(() => {
    if (!user?.address) {
      setEarningsSol(0);
      return;
    }
    aggregateCallRecords('sum', {
      filter: `creatorWallet='${user.address}'`,
      field: 'creatorAmount',
    }).then((res) => setEarningsSol(lamportsToSol(res?.value ?? 0)));
  }, [user?.address]);

  const activeAgents = agents?.length ?? 0;
  const previewAgents = (agents ?? [])
    .slice()
    .sort((a, b) => Number(isFunctionalAgentId(b.id) || b.isFunctional) - Number(isFunctionalAgentId(a.id) || a.isFunctional))
    .slice(0, 5);

  return (
    <PageLayout background="" fullBleed footer={false}>
      <TopNav />

      <div className="container pb-24 lg:pb-10">
        {/* ── Greeting ── */}
        <header className="pt-10 pb-2 animate-fade-in-up">
          <p className="text-sm font-medium text-muted-foreground">
            {timeGreeting()}
            {user ? ',' : ''}
          </p>
          <h1 className="text-headline font-extrabold tracking-tight mt-1">Agent Command Center</h1>
        </header>

        {/* ── Quick actions ── */}
        <div className="flex items-center gap-6 sm:gap-10 mt-8 overflow-x-auto pb-1 animate-fade-in-up delay-1">
          <QuickAction icon={<Bot className="w-5 h-5" />} label="Browse" onClick={() => navigate('/agents')} />
          <QuickAction
            icon={<LayoutGrid className="w-5 h-5" />}
            label="My Agents"
            onClick={() => document.getElementById('my-agents')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
          />
          <QuickAction
            icon={<KeyRound className="w-5 h-5" />}
            label="Connect API"
            onClick={() => toast.info('Bring your own API agents — coming soon')}
          />
          <QuickAction icon={<Plus className="w-5 h-5" />} label="Create Agent" onClick={() => navigate('/create')} dark />
        </div>

        {/* ── Stat strip ── */}
        <div className="grid grid-cols-3 gap-3 mt-10 animate-fade-in-up delay-2">
          <StatBlock label="Active Agents" value={activeAgents} progress={Math.min(activeAgents / 12, 1)} />
          <StatBlock label="Total Calls" value={totalCalls} progress={Math.min(totalCalls / 100, 1)} />
          <StatBlock label="Your Earnings" value={earningsSol} suffix="SOL" decimals={4} progress={Math.min(earningsSol / 0.01, 1)} />
        </div>

        {/* ── Active agents ── */}
        <section className="mt-10 animate-fade-in-up delay-3">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-bold text-lg">Active agents</h2>
            <button
              onClick={() => navigate('/agents')}
              className="text-sm font-semibold text-primary transition-transform duration-300 hover:translate-x-0.5"
            >
              View all →
            </button>
          </div>
          <div className="space-y-3">
            {previewAgents.map((agent) => (
              <AgentListRow key={agent.id} agent={agent} />
            ))}
            {previewAgents.length === 0 && <p className="text-sm text-muted-foreground">No agents listed yet.</p>}
          </div>
        </section>

        {/* ── Your agents (creator) ── */}
        <section id="my-agents" className="mt-12 pt-2 animate-fade-in-up delay-4">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-bold text-lg">Your agents</h2>
            <button
              onClick={() => navigate('/create')}
              className="text-sm font-semibold text-primary inline-flex items-center gap-1 transition-transform duration-300 hover:translate-x-0.5"
            >
              <Plus className="w-3.5 h-3.5" /> New agent
            </button>
          </div>

          {!user && (
            <EmptyState
              icon={<Wallet />}
              title="Connect your wallet"
              description="Sign in to create no-code agents and track what they earn you."
              action={
                <button
                  onClick={() => login()}
                  className="rounded-full bg-[#00C805] text-white font-bold px-6 py-3 hover:bg-[#00b004] transition-colors"
                >
                  Connect wallet
                </button>
              }
            />
          )}

          {user && (myAgents ?? []).length === 0 && (
            <EmptyState
              icon={<Inbox />}
              title="No agents yet"
              description="Publish your first no-code agent and start earning SOL per call."
              action={
                <button
                  onClick={() => navigate('/create')}
                  className="rounded-full bg-[#00C805] text-white font-bold px-6 py-3 hover:bg-[#00b004] transition-colors"
                >
                  Create your first agent
                </button>
              }
            />
          )}

          {user && (myAgents ?? []).length > 0 && (
            <div className="space-y-3">
              {(myAgents ?? []).map((agent) => (
                <CreatorAgentRow key={agent.id} agent={agent} />
              ))}
            </div>
          )}
        </section>
      </div>
    </PageLayout>
  );
};

const QuickAction: React.FC<{ icon: React.ReactNode; label: string; onClick: () => void; dark?: boolean }> = ({
  icon,
  label,
  onClick,
  dark = false,
}) => (
  <button onClick={onClick} className="flex flex-col items-center gap-2 group shrink-0">
    <span
      className={`w-14 h-14 rounded-full flex items-center justify-center transition-all duration-300 group-hover:-translate-y-1 group-hover:shadow-lg ${
        dark
          ? 'bg-foreground text-background shadow-md'
          : 'bg-card border border-border text-foreground group-hover:border-primary group-hover:text-primary'
      }`}
    >
      {icon}
    </span>
    <span className="text-[11px] font-semibold text-muted-foreground group-hover:text-foreground transition-colors whitespace-nowrap">
      {label}
    </span>
  </button>
);

const StatBlock: React.FC<{ label: string; value: number; progress: number; suffix?: string; decimals?: number }> = ({
  label,
  value,
  progress,
  suffix,
  decimals = 0,
}) => (
  <div className="rounded-2xl bg-card border border-border p-4 shadow-sm">
    <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wide">{label}</p>
    <div className="mt-1.5 text-xl font-extrabold flex items-baseline gap-1">
      <NumberTicker value={value} decimalPlaces={decimals} />
      {suffix && <span className="text-xs font-bold text-primary">{suffix}</span>}
    </div>
    <div className="mt-3 h-1.5 rounded-full bg-secondary overflow-hidden">
      <div
        className="h-full rounded-full bg-[#00C805] transition-all duration-700"
        style={{ width: `${Math.max(progress * 100, 3)}%` }}
      />
    </div>
  </div>
);

const AgentListRow: React.FC<{ agent: AgentsResponse }> = ({ agent }) => {
  const navigate = useNavigate();
  return (
    <button
      onClick={() => navigate(`/agents/${agent.id}`)}
      className="w-full flex items-center gap-3 rounded-2xl bg-card border border-border p-3.5 text-left transition-all duration-300 hover:border-primary/50 hover:shadow-md hover:-translate-y-0.5"
    >
      <AgentAvatar avatarUrl={agent.avatarUrl} avatarGradient={agent.avatarGradient} name={agent.name} size="md" />
      <div className="flex-1 min-w-0">
        <p className="font-bold truncate">{agent.name}</p>
        <p className="text-xs text-muted-foreground truncate mt-0.5">{agent.description}</p>
        <div className="flex items-center gap-1.5 mt-1.5 text-[11px] text-muted-foreground">
          <span className="w-1.5 h-1.5 rounded-full bg-[#00C805]" />
          {formatCount(agent.statsRuns)} runs today
        </div>
      </div>
      <span className="text-xs font-bold text-primary shrink-0">{formatSol(agent.pricePerCall)}</span>
    </button>
  );
};

const CreatorAgentRow: React.FC<{ agent: AgentsResponse }> = ({ agent }) => {
  const navigate = useNavigate();
  const [deleting, setDeleting] = useState(false);

  const handleDelete = async () => {
    setDeleting(true);
    try {
      const success = await deleteAgents(agent.id);
      if (success) {
        toast.success('Agent deleted.');
      } else {
        toast.error('Could not delete this agent. Please try again.');
      }
    } catch (err) {
      console.error(err);
      toast.error('Could not delete this agent. Please try again.');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="w-full flex items-center gap-2 rounded-2xl bg-card border border-border p-3.5 transition-all duration-300 hover:border-primary/50 hover:shadow-md">
      <button
        onClick={() => navigate(`/agents/${agent.id}`)}
        className="flex items-center gap-3 flex-1 min-w-0 text-left"
      >
        <AgentAvatar avatarUrl={agent.avatarUrl} avatarGradient={agent.avatarGradient} name={agent.name} size="md" />
        <div className="flex-1 min-w-0">
          <p className="font-bold truncate flex items-center gap-1.5">
            {agent.name}
            {agent.systemPrompt && <Sparkles className="w-3.5 h-3.5 text-primary shrink-0" />}
          </p>
          <p className="text-xs text-muted-foreground truncate mt-0.5">
            {formatCount(agent.statsRuns)} runs · {formatSol(agent.pricePerCall)}/call
          </p>
        </div>
      </button>

      <span className="text-sm font-extrabold text-primary shrink-0">
        {formatSol(agent.statsVolume * (agent.creatorSplitPct / 100), 4)}
      </span>

      <div className="flex items-center gap-1 shrink-0 pl-2 border-l border-border ml-1">
        <button
          onClick={() => navigate(`/agents/${agent.id}/edit`)}
          aria-label="Edit agent"
          className="p-2 rounded-full text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
        >
          <Pencil className="w-3.5 h-3.5" />
        </button>

        <AlertDialog>
          <AlertDialogTrigger asChild>
            <button aria-label="Delete agent" className="p-2 rounded-full text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors">
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete "{agent.name}"?</AlertDialogTitle>
              <AlertDialogDescription>
                This permanently removes the agent from the marketplace. Renters will no longer be able to hire or chat with it. This
                cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDelete}
                disabled={deleting}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90 flex items-center gap-1.5"
              >
                {deleting && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                {deleting ? 'Deleting…' : 'Delete'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
};

export default DashboardHomePage;
